import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useCallback, useContext, useMemo, useRef, useState, } from 'react';
import { optionBorderWidth, optionPadding, selectedOptionBorderWidth, selectedOptionPadding, } from '../../styles/border';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { PickerOptionInputStateContext } from '../../utils/InputStateContext';
import { useKeyboardFocusState } from '../../utils/useKeyboardFocusState';
import { PickerContext } from './PickerContext';
const customPickerStyles = css `
  padding-bottom: 0;
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  border-width: 0;
`;
const PickerOption = ({ ariaDescribedBy, ariaLabel, ariaLabelledBy, children, disabled, id, value, }) => {
    const { isLoading, modifier, name, onChange, optionType, selectedOptions, } = useContext(PickerContext);
    const inputRef = useRef(null);
    const isKeyboardFocused = useKeyboardFocusState(inputRef);
    const isSelected = useMemo(() => (Array.isArray(selectedOptions)
        ? selectedOptions.includes(value)
        : selectedOptions === value), [
        selectedOptions,
        value,
    ]);
    const [isHovering, setIsHovering,] = useState(false);
    const labelStyles = useMemo(() => (css `
        line-height: 0;
        ${isKeyboardFocused && styleUtils.focusVisibleStyles}
      `), [
        isKeyboardFocused,
    ]);
    const optionContainerStyles = useMemo(() => (css `
        ${styleUtils.borderBox}
        background-color: ${theme.colors.gray100};
        border-color: ${theme.colors.gray400};
        border-style: solid;
        border-width: ${styleUtils.rem(optionBorderWidth)};
        display: inline-block;
        line-height: 0;
        padding-bottom: ${optionPadding};
        padding-left: ${optionPadding};
        padding-right: ${optionPadding};
        padding-top: ${optionPadding};
        width: 100%;
        ${modifier === 'swatch' && css `
          border-color: transparent;
        `}
        ${isSelected && css `
          border-color: ${theme.colors.gray700};
          border-width: ${styleUtils.rem(selectedOptionBorderWidth)};
          padding-bottom: ${styleUtils.rem(selectedOptionPadding)};
          padding-left: ${styleUtils.rem(selectedOptionPadding)};
          padding-right: ${styleUtils.rem(selectedOptionPadding)};
          padding-top: ${styleUtils.rem(selectedOptionPadding)};
        `}
        ${modifier === 'none' && customPickerStyles}

        &:active,
        &:focus,
        &:hover {
          border-color: ${theme.colors.gray700};
          border-width: ${styleUtils.rem(optionBorderWidth)};
          cursor: pointer;
          padding-bottom: ${optionPadding};
          padding-left: ${optionPadding};
          padding-right: ${optionPadding};
          padding-top: ${optionPadding};
          ${isSelected && css `
            border-color: ${theme.colors.gray700};
            border-width: ${styleUtils.rem(selectedOptionBorderWidth)};
            padding-bottom: ${styleUtils.rem(selectedOptionPadding)};
            padding-left: ${styleUtils.rem(selectedOptionPadding)};
            padding-right: ${styleUtils.rem(selectedOptionPadding)};
            padding-top: ${styleUtils.rem(selectedOptionPadding)};
          `}
          ${modifier === 'none' && customPickerStyles}
        }

        ${(isLoading || disabled) && css `
          border-color: ${theme.colors.gray400};
          color: ${theme.colors.textDisabled};
          cursor: default;

          &:hover {
            border-color: ${theme.colors.gray400};
            cursor: default;
          }
        `}
        ${((isLoading || disabled) && modifier === 'swatch') && css `
          border-color: transparent;

          &:hover {
            border-color: transparent;
          }
        `}
      `), [
        disabled,
        isLoading,
        isSelected,
        modifier,
    ]);
    const optionStyles = useMemo(() => (css `
        ${styleUtils.borderBox}
        border-color: transparent;
        border-style: solid;
        border-width: ${styleUtils.rem(optionBorderWidth)};
        display: inline-block;
        width: 100%;

        ${modifier === 'swatch' && css `
          border-color: ${theme.colors.gray400};
        `}
        ${modifier === 'none' && css `
          border-width: 0;
        `}
      `), [
        modifier,
    ]);
    const inputId = useMemo(() => (id || `pickerOption-${value === null || value === void 0 ? void 0 : value.replace(' ', '_')}-${Math.random()}`), [
        id,
        value,
    ]);
    const onPointerEnter = useCallback(() => {
        setIsHovering(true);
    }, []);
    const onPointerLeave = useCallback(() => {
        setIsHovering(false);
    }, []);
    const providerValue = useMemo(() => ({
        isDisabled: disabled,
        isFocusVisible: isKeyboardFocused,
        isHovering,
        isSelected,
    }), [
        disabled,
        isKeyboardFocused,
        isHovering,
        isSelected,
    ]);
    return (_jsxs("label", Object.assign({ css: labelStyles, htmlFor: inputId, onPointerEnter: onPointerEnter, onPointerLeave: onPointerLeave }, { children: [_jsx("input", { "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : value, "aria-labelledby": ariaLabelledBy, checked: isSelected, css: styleUtils.visuallyHidden, disabled: isLoading || disabled, id: inputId, name: name, onChange: onChange, ref: inputRef, type: optionType, value: value }), _jsx("span", Object.assign({ css: optionContainerStyles }, { children: _jsx("span", Object.assign({ "aria-checked": isSelected, css: optionStyles, role: optionType }, { children: _jsx(PickerOptionInputStateContext.Provider, Object.assign({ value: providerValue }, { children: children })) })) }))] })));
};
const MemoizedPickerOption = memo(PickerOption);
export { MemoizedPickerOption as PickerOption };
// MC-1068 Temporary hack for Storybook.
export default PickerOption;
